import React, {useCallback, useEffect} from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import {useUserAPI} from "@src/api/user.api";
import {User} from "@src/model/user";
import {useNavigate} from "react-router-dom";
import Alert, {AlertProps} from "../../common/alert";
import _ from "lodash"; // Make sure lodash is imported

interface UserIndexProps { }
const UserIndex: React.FC<UserIndexProps> = (props) => {
  const [data, setData] = React.useState<User[]>([]);
  const [allUsers, setAllUsers] = React.useState<User[]>([]);
  const [search, setSearch] = React.useState("");
  const [hideDisabled, setHideDisabled] = React.useState<boolean>(false);
  const [status, setStatus] = React.useState<AlertProps | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const userAPI = useUserAPI();
  const navigate = useNavigate();
  const columns = [
    {
      name: "Name",
      selector: (row: any) => row.full_name,
      sortable: true,
      width: "150px",
    },
    {
      name: "Email",
      selector: (row: any) => row.email,
      sortable: true,
      width: "200px",
    },
    {
      name: "Active",
      selector: (row: any) => row.is_active,
      sortable: true,
      sortField: "is_active",
      cell: (row: any) => row.is_active ? <span className="text-green-500">Active</span> : <span className="text-red-500">DISABLED</span>,
      width: "100px",
    },
    {
      name: "User Type",
      selector: (row: any) => row.user_type,
      sortable: true,
      width: "150px",
    },
    {
      name: "Industry",
      selector: (row: any) => row.industry_type,
      sortable: true,
      width: "150px",
    },
    {
      name: "Last Login",
      selector: (row: any) => row.last_login || "",
      sortable: true,
      format: (row: any) => {
        return row.last_login ? new Date(row.last_login).toLocaleDateString() : "Never";
      },
      width: "120px",
    },
    {
      name: "Organization",
      selector: (row: any) => row.organization_name || "",
      sortable: true,
      width: "120px",
    },

      {
          name: "Vaults",
          selector: (row: any) => row.num_vaults,
          sortable: true,
          cell: (row: any) => {
            return <a href={`/vaults?user_id=${row.id}`} style={{textDecoration: "underline"}}>View</a>;
          },
          width: "90px",
      },

    {
      name: "Actions",
      cell: (row: any) => (
        <div className="flex gap-2 flex-nowrap min-w-[24em]">
          <a
            href={`/users/edit/${row.id}`}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-2 rounded"
          >
            Edit
          </a>
          {row.user_type !== "admin" && <a
            target="_blank"
            rel="noopener noreferrer"
            href={`${process.env.REACT_APP_BASE_URL}/dashboard?imp=${row.id}`}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-2 rounded"
          >
            Impersonate
          </a>}
        </div>
      ),
      width: "300px",
    },
  ];
  const debouncedFetchUsers = useCallback(
    _.debounce(() => {
      setLoading(true);
      userAPI
        .getUsers(search, 1000, 0)
        .then((response) => {
          setAllUsers(response);
          const filteredData = hideDisabled ? response.filter(user => user.is_active) : response;
          setData(filteredData);
          setLoading(false);
        })
        .catch((error) => {
          setStatus({ type: "error", message: "Failed to load users: " + error.message });
          setLoading(false);
        });
    }, 500),
    [search, userAPI]
  );

  useEffect(() => {
    const filteredData = hideDisabled ? allUsers.filter(user => user.is_active) : allUsers;
    setData(filteredData);
  }, [hideDisabled, allUsers]);

  useEffect(() => {
    debouncedFetchUsers();
    return () => {
      debouncedFetchUsers.cancel();
    };
  }, [search]);

  return (
    <AppContainer>
      <div className="container">
        <div className="flex justify-between">
          <h1 className="text-3xl">Users</h1>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            onClick={() => navigate("/users/add")}
          >
            Add User
          </button>
        </div>
        {status && (
          <div className="mb-4">
            <Alert {...status} onClose={() => setStatus(null)} />
          </div>
        )}
        <form className="w-full mx-autom mt-2">
          <label htmlFor="default-search" className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white">
            Search
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                />
              </svg>
            </div>
            <input
              type="search"
              id="default-search"
              onChange={(val) => setSearch(val.target.value)}
              className="block w-full p-4 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Search name, email ..."
              required
            />
          </div>
          <div className="w-full flex justify-end gap-2 mt-2 items-center">
            <input
              id="hide-disabled"
              type="checkbox"
              checked={hideDisabled}
              onChange={(e) => setHideDisabled(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label htmlFor="hide-disabled" className=" text-sm font-medium text-gray-900 dark:text-gray-300">
              Hide disabled users
            </label>
          </div>

        </form>
        <DataTable
          columns={columns}
          data={data}
          pagination
          progressPending={loading}
          progressComponent={<div className="py-6">Loading users...</div>}
          noDataComponent={<div className="py-6">No users found</div>}
        />
      </div>
    </AppContainer>
  );
};

export default UserIndex;
